import PageHeader from "@/components/PageHeader";
import CTASection from "@/components/CTASection";
import { FlaskConical, Beaker, Microscope, TestTubes, Atom, Sparkles } from "lucide-react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const areas = [
  {
    Icon: FlaskConical,
    title: "Applied Research",
    d: "Translating scientific principles into practical engineering solutions across mechatronics, AI and aerospace.",
  },
  {
    Icon: Microscope,
    title: "Scientific Development",
    d: "Fundamental studies in motors, control systems, materials and sensors that feed our product engineering.",
  },
  {
    Icon: Beaker,
    title: "Prototype Development",
    d: "Rapid electromechanical and firmware prototyping — from concept to functional demonstrators.",
  },
  {
    Icon: TestTubes,
    title: "Experimental Technologies",
    d: "Exploratory programs in edge AI, autonomous flight and space instrumentation.",
  },
  {
    Icon: Atom,
    title: "Product Engineering",
    d: "Design-for-manufacture, qualification, EMC/EMI and reliability engineering.",
  },
  {
    Icon: Sparkles,
    title: "Technology Commercialization",
    d: "Bringing research-grade products to industry with productization and IP strategy.",
  },
];

const Research = () => {
  useDocumentTitle("Research");
  return (
    <div data-testid="research-page">
      <PageHeader
        eyebrow="Research & Innovation"
        title="Curiosity at the core. Engineering at the edge."
        description="Our multi-disciplinary research groups explore scientific frontiers and translate them into engineered products, systems and platforms."
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Research" }]}
      />

      <section className="py-20 lg:py-24 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {areas.map(({ Icon, title, d }) => (
            <div
              key={title}
              className="bg-white border border-slate-200 rounded-xl p-7 hover:shadow-lg hover:-translate-y-0.5 hover:border-sky-300 transition-all"
            >
              <div className="w-11 h-11 rounded-md bg-[#0B1B3D]/5 border border-slate-200 flex items-center justify-center mb-5">
                <Icon className="w-5 h-5 text-[#0B1B3D]" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-lg font-semibold text-[#0B1B3D]">
                {title}
              </h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="py-20 lg:py-24 bg-[#0B1B3D] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dots opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight max-w-2xl">
            From hypothesis to production, in six deliberate steps.
          </h2>
          <div className="mt-12 grid md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              "Hypothesis",
              "Literature & Simulation",
              "Prototype",
              "Validation",
              "Productization",
              "Deployment",
            ].map((s, i) => (
              <div
                key={s}
                className="relative border border-white/10 rounded-lg p-5 bg-white/[0.03]"
              >
                <div className="font-mono-akyvia text-amber-400 text-xs">
                  0{i + 1}
                </div>
                <div className="mt-2 font-display font-semibold text-white">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="py-16" />
      <CTASection
        eyebrow="Collaborate"
        title="Explore a joint research program with Akyvia."
      />
      <div className="py-24" />
    </div>
  );
};

export default Research;
